import React, { useMemo, useState } from 'react';
import { useQuery } from '@common/hooks';
import { toUSCompactDateFormat } from 'works/legacy/lib/date-helper';
import {
  Currency,
  DateRangeFilter,
  FilteredTable,
  MultiSelectTableFilter,
  NumberRangeFilter,
  RenderTableColumn,
  RenderTableOnChange,
  TextFilterSearch,
} from '@common/components';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import {
  InvoiceItemListFragmentType,
  InvoiceItemsFiltersQuery,
  InvoiceItemsFiltersQueryArgs,
  InvoiceItemsFiltersQueryResponse,
  PaginatedInvItemsListQuery,
  PaginatedInvItemsListQueryArgs,
  PaginatedInvItemsListQueryResponse,
} from '@pay-bill/graphql';
import {
  DisputeActionsDropdown,
  INVOICE_ITEM_LABEL_OVERWRITE,
  InvoiceItemDisputeState,
  TotalCountLabel,
} from '@pay-bill/components';
import { CLEANUP_WHEN_EMPTY, nonFalse } from '@pay-bill/services';
import { capitalize, filter } from 'lodash';
import Filters from 'works/legacy/lib/filters';
import { formatSecondsToDuration } from '@timekeeping/services';
import { titleize } from '@common/services';
import { ENUM_QUERY } from '@common/graphql';
import { handleEmploymentTypeFilter } from '@clinician-profile/services';

const LOCALSTORAGE_FILTER_KEY = 'InvoiceItemsTable';

export const BillingInvoiceItemsList = ({
  entity,
  viewMode,
}: {
  entity: {
    id: string;
  };
  viewMode: 'Organization' | 'Supplier';
}) => {
  const [filters, setFilters] = useState<
    PaginatedInvItemsListQueryArgs['filters'] & {
      employmentType?: string[];
    }
  >(() => Filters.getFilters(LOCALSTORAGE_FILTER_KEY));

  const onFilterChange = (filters: { [key: string]: unknown }) => {
    setFilters(filters);
    Filters.setFilters(LOCALSTORAGE_FILTER_KEY, filters);
  };

  const [pageData, setPageData] = useState<
    PaginatedInvItemsListQueryArgs['page']
  >(() => ({
    limit: 50,
    offset: 0,
    orderDirection: 'ASC',
    orderField: 'TRANSACTION_ID_INDICATOR',
  }));

  const { data: filterValues, loading: filtersLoading } = useQuery<
    InvoiceItemsFiltersQueryResponse,
    InvoiceItemsFiltersQueryArgs
  >(InvoiceItemsFiltersQuery, {
    variables: {
      entityId: entity.id,
      entityType: viewMode === 'Organization' ? 'invoice' : 'reverse_invoice',
    },
  });

  const { data, loading } = useQuery<
    PaginatedInvItemsListQueryResponse,
    PaginatedInvItemsListQueryArgs
  >(PaginatedInvItemsListQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      entityId: entity.id,
      entityType: viewMode === 'Organization' ? 'invoice' : 'reverse_invoice',
      page: pageData,
      filters: filters,
    },
  });

  const { data: employmentTypesData, loading: employmentTypesLoading } =
    useQuery(ENUM_QUERY, { variables: { key: 'EmploymentTypes' } });

  const availableFilters = useMemo(() => {
    const values = filterValues?.values;

    return {
      states: values?.disputeStates || [],
      transactionIds: values?.transactionIds || [],
    };
  }, [filterValues?.values]);

  const page = data?.page;

  const { items, total } = useMemo(
    () => ({
      total: page?.totalCount ?? 0,
      items: (page?.items || []).map((item) => {
        let rowClassName;
        if (item.transactionId !== 'NEW') {
          rowClassName = 'transaction-id-row';
        }
        return { ...item, rowClassName };
      }),
    }),
    [page]
  );

  const reverseInvoiceItemColumns = filter<
    RenderTableColumn<InvoiceItemListFragmentType> | false,
    RenderTableColumn<InvoiceItemListFragmentType>
  >(
    [
      {
        title: 'Name',
        dataIndex: 'worksProfile',
        key: 'fullName',
        className: 'worker-full-name-col sentry-mask',
        sorter: true,
        filtered: !!filters.fullName?.length,
        filteredValue: filters.fullName || CLEANUP_WHEN_EMPTY,
        render: (profile) => <div>{profile.fullName}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              placeholder="Filter by Name"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Employee ID',
        dataIndex: 'organizationProfile',
        key: 'employeeId',
        className: 'employee-id-col',
        sorter: true,
        filtered: !!filters.employeeId?.length,
        filteredValue: filters.employeeId || CLEANUP_WHEN_EMPTY,
        render: (orgProfile) => <div>{orgProfile.employeeId}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              placeholder="Filter by Employee ID"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      viewMode !== 'Supplier' && {
        title: 'Supplier',
        dataIndex: 'supplier',
        key: 'supplier',
        className: 'supplier-col',
        sorter: true,
        filtered: !!filters.supplier?.length,
        filteredValue: filters.supplier || CLEANUP_WHEN_EMPTY,
        render: (supplier) => <div>{supplier.name}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              placeholder="Filter by Supplier"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Pay Code',
        key: 'payCode',
        className: 'pay-code-col',
        sorter: true,
        filtered: !!filters.payCode?.length,
        filteredValue: filters.payCode || CLEANUP_WHEN_EMPTY,
        filterFormat: 'id',
        dataIndex: 'payCode',
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              placeholder="Filter by Pay Code"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Date',
        dataIndex: 'date',
        key: 'date',
        className: 'item-date-col',
        sorter: true,
        filterFormat: 'dateRange',
        filtered: !!filters.date?.length,
        filteredValue: (filters.date as string[]) || CLEANUP_WHEN_EMPTY,
        render: (date) => <div>{toUSCompactDateFormat(date)}</div>,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <DateRangeFilter
              autoFocus
              onSearch={(value) => {
                setSelectedKeys((value || []) as string[]);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Rate',
        dataIndex: 'effectiveHourlyRateCents',
        key: 'effectiveHourlyRateCents',
        render: (value) => <Currency value={value / 100} />,
      },
      {
        title: 'Employment',
        dataIndex: 'staffingRequestAssignment',
        key: 'employmentType',
        sorter: true,
        filtered: !!filters.employmentType?.length,
        filteredValue: filters.employmentType || null,
        filters: handleEmploymentTypeFilter(employmentTypesData),
        filterDropdown: MultiSelectTableFilter,
        render: (_, record) => (
          <>{titleize(record.staffingRequestAssignment.employmentType)}</>
        ),
      },
      {
        title: 'Hours',
        dataIndex: 'totalSeconds',
        key: 'hours',
        className: 'item-hours-col',
        sorter: true,
        filterFormat: 'hoursRange',
        filtered: !!filters.hours?.length,
        filteredValue: filters.hours || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <NumberRangeFilter
              onSearch={(value) => {
                value ? setSelectedKeys(value) : setSelectedKeys([]);
                confirm();
              }}
            />
          </section>
        ),
        render: (totalSeconds) => (
          <div>{formatSecondsToDuration(totalSeconds)}</div>
        ),
      },
      {
        title: 'Cost Center',
        dataIndex: 'costId',
        key: 'costId',
        className: 'cost-id-col',
        sorter: true,
        filtered: !!filters.costId?.length,
        filteredValue: filters.costId || CLEANUP_WHEN_EMPTY,
        filterDropdown: ({ setSelectedKeys, confirm }) => (
          <section style={{ padding: `${Spacing.baseSpacing / 2}px` }}>
            <TextFilterSearch
              placeholder="Filter by Cost Center"
              onSearch={(value) => {
                setSelectedKeys(value ? [value] : []);
                confirm();
              }}
            />
          </section>
        ),
      },
      {
        title: 'Billing Amount',
        dataIndex: 'totalCents',
        key: 'billingAmount',
        render: (totalCents) => <Currency value={totalCents / 100} />,
      },
      {
        title: 'Dispute Status',
        dataIndex: 'dispute',
        key: 'disputeStatus',
        className: 'dispute-status-col',
        sorter: true,
        filtered: !!filters.disputeStatus?.length,
        filteredValue: filters.disputeStatus || CLEANUP_WHEN_EMPTY,
        filters: availableFilters.states.map((filter) => ({
          text: INVOICE_ITEM_LABEL_OVERWRITE[filter] || filter,
          value: filter,
        })),
        render: (dispute, item) => {
          if (item.transactionId !== 'NEW') {
            return;
          }

          return (
            <InvoiceItemDisputeState
              state={dispute?.state || 'no_dispute'}
              createdBy={dispute?.createdBy?.entityType}
            />
          );
        },
      },
      {
        title: 'Type',
        dataIndex: 'transactionId',
        key: 'transactionId',
        className: 'transaction-id-col',
        sorter: true,
        filtered: !!filters.transactionId?.length,
        filteredValue: filters.transactionId || CLEANUP_WHEN_EMPTY,
        filters: availableFilters.transactionIds.map((filter) => ({
          text: capitalize(filter),
          value: filter,
        })),
        render: capitalize,
      },
      {
        title: 'Action',
        key: 'availableAction',
        className: 'dispute-action-col',
        filtered: !!filters.availableAction?.length,
        filteredValue: filters.availableAction || CLEANUP_WHEN_EMPTY,
        filters: [
          { text: 'View Dispute', value: 'view_dispute' },
          { text: 'Dispute', value: 'submit_dispute' },
          { text: 'No Action', value: 'none' },
        ],
        render: (_: unknown, item: InvoiceItemListFragmentType) => {
          const showActions =
            item.canSubmitDispute.value || item.resolvedDispute || item.dispute;

          if (showActions) {
            return (
              <DisputeActionsDropdown listType="invoiceItems" item={item} />
            );
          }
        },
      },
    ],
    nonFalse
  );

  const tablePageInfo = useMemo(
    () => ({
      ...pageData,
      showSizeChanger: true,
      defaultPageSize: pageData.limit,
      total,
    }),
    [pageData, total]
  );

  const handleTableChange: RenderTableOnChange<InvoiceItemListFragmentType> = (
    pagination,
    filters,
    sorter
  ) => {
    if (sorter instanceof Array) {
      return;
    }
    const pageSize = pagination.pageSize || tablePageInfo.defaultPageSize;
    const orderDirection = sorter.order === 'descend' ? 'DESC' : 'ASC';

    const orderMap: Record<
      string,
      PaginatedInvItemsListQueryArgs['page']['orderField']
    > = {
      fullName: 'FULL_NAME',
      code: 'ID',
      disputeStatus: 'DISPUTE_STATUS',
      date: 'DATE',
      hours: 'HOURS',
      payCode: 'PAY_CODE',
      employeeId: 'EMPLOYEE_ID',
      transactionId: 'TRANSACTION_ID',
      supplier: 'SUPPLIER',
      costId: 'COST_ID',
    };
    const orderField =
      orderMap[sorter.columnKey as string | number] ||
      'TRANSACTION_ID_INDICATOR';

    onFilterChange(filters);
    setPageData(() => ({
      orderField,
      orderDirection,
      offset: ((pagination?.current || 1) - 1) * pageSize,
      limit: pageSize,
    }));
  };

  return (
    <>
      <TotalCountLabel
        dataLength={items.length}
        totalCount={total}
        label="line items"
      />
      <FilteredTable
        rowKey="id"
        className="invoice-items-list"
        dataSource={items}
        columns={reverseInvoiceItemColumns}
        loading={loading || filtersLoading || employmentTypesLoading}
        onChange={handleTableChange}
        pagination={tablePageInfo}
        localStorageFilterKey={LOCALSTORAGE_FILTER_KEY}
        onFilterChange={onFilterChange}
        filters={filters}
      />
      <style jsx>{`
        :global(.invoice-items-list .ant-table-row) {
          height: 49px;
        }
        :global(.transaction-id-row) {
          background: ${Colors.steelLightest};
        }
        :global(.dispute-button-spacing) {
          max-width: 116px;
          display: flex;
          justify-content: flex-end;
        }
      `}</style>
    </>
  );
};
